import React, { useState } from 'react';
import { Grid, ClickAwayListener, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import CustomTimePicker from 'src/screens/Control/ControlComponents/BacnetSchedule/AddEditSchedule/CustomTimePicker';
import addEditScheduleStyles from '../addEditSchedule.style';

const useDefaultStyles = makeStyles(addEditScheduleStyles);

/**
 * Interface for the classes object required by TimePickerRow.
 */
export interface TimePickerRowClasses {
  /** Root container for the row */
  container?: string;
  /** Specific container for the time picker */
  timePickerContainer?: string;
  /** Class applied when the time picker is open */
  timePickerContainerOpen?: string;
}

/**
 * Props for the TimePickerRow component.
 */
interface TimePickerRowProps {
  /** Unique identifier for the picker */
  id: string;
  /** Current time value as a string (e.g., "12:00") */
  currentValue: string;
  /** Label to display for the row */
  label: string | React.ReactElement;
  /** Callback triggered when the time changes */
  handleTimeChange: (time: string) => void;
  /** Whether to use 12-hour format */
  is12Hours?: boolean;
  /** Whether the row is active (displays checkbox if provided) */
  isActive?: boolean;
  /** Callback triggered when the active state changes */
  handleActiveChange?: () => void;
  /** Whether to hide the time display and picker */
  hideTimePicker?: boolean;
  /** Optional classes to override default styles */
  classes?: TimePickerRowClasses;
  /** Additional class name for the root Grid container */
  className?: string;
}

/**
 * A reusable row component that contains a CustomTimePicker wrapped in a Grid and ClickAwayListener.
 * Handles its own open/close state for the time picker.
 */
const TimePickerRow: React.FC<TimePickerRowProps> = ({
  classes: providedClasses,
  className,
  id,
  ...props
}) => {
  const defaultClasses = useDefaultStyles();
  const classes = providedClasses || defaultClasses;
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Grid container className={clsx(classes.container, classes.timePickerContainer, isOpen && classes.timePickerContainerOpen, className)}>
        <CustomTimePicker
          {...props}
          id={id}
          activePickerId={isOpen ? id : null}
          handlePickerClick={handleToggle}
        />
      </Grid>
    </ClickAwayListener>
  );
};

export default TimePickerRow;
